import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useApiError } from '@/hooks/useApiError';
import { emailOtpService } from '@/services/emailOtpService';
import type { User } from '@/types/auth';

/** Matches the backend's per-email SEND_COOLDOWN_SECONDS. */
export const EMAIL_CODE_RESEND_SECONDS = 60;
export const EMAIL_CODE_LENGTH = 6;

/**
 * Espotz's own email-code login (not Sequence): used where Sequence is blocked, and as the dev
 * "Can't sign in?" fallback, like the app's email-code screen. `send` starts the client cooldown;
 * `verify` resolves with the user on the right code.
 */
export function useBackendEmailOtp() {
  const queryClient = useQueryClient();
  const { handleApiError } = useApiError();
  const { t: tCommon } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  useEffect(() => {
    if (resendSecondsLeft <= 0) return;
    const timer = setTimeout(() => setResendSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendSecondsLeft]);

  const send = useCallback(
    async (email: string): Promise<boolean> => {
      if (resendSecondsLeft > 0) return false;
      setIsLoading(true);
      try {
        await emailOtpService.sendOtp(email);
        setResendSecondsLeft(EMAIL_CODE_RESEND_SECONDS);
        toast.success(tCommon('authModal.otpSentToast'));
        return true;
      } catch (error) {
        handleApiError(error, {
          defaultMessage: tCommon('authModal.otpSendFailed'),
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, resendSecondsLeft, tCommon],
  );

  const verify = useCallback(
    async (email: string, code: string): Promise<User | null> => {
      setIsLoading(true);
      try {
        const response = await emailOtpService.verifyOtp(email, code);
        await queryClient.invalidateQueries({ queryKey: ['user'] });
        toast.success(tCommon('authModal.loginSuccess'));
        return response.result?.user ?? null;
      } catch (error) {
        handleApiError(error, {
          defaultMessage: tCommon('authModal.otpVerifyFailed'),
        });
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, queryClient, tCommon],
  );

  return { send, verify, isLoading, resendSecondsLeft };
}
